
// CORE
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// MUI
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import ButtonBase from '@material-ui/core/ButtonBase';
import ExitIcon from '@material-ui/icons/Clear';

// Data
import { useSelector, useDispatch } from 'react-redux'
import { removeDialog } from 'data/rootUi/actions';
import { frequencyAliasFromRecurrence } from 'data/scheduledTransactions/scheduledTransactionsUtils';
import { getScheduledTransactionById } from 'data/scheduledTransactions/scheduledTransactionsSelectors';
import { isIncomeTxn } from 'data/transactions/selectors';

// Custom
import QButton from 'components/QButton';
import SeriesBarChart from 'components/Upcoming/seriesBarChart';
import ViewRow from 'components/Upcoming/viewRow';
import ScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog';
import { formatNumber } from 'components/QuickenControls/AmountField';
import { generateChartData, getModelHistory } from 'containers/Upcoming/selectors';
import SkeletonRow from 'components/Upcoming/skeleton';
import AlertDialog from '../AlertDialog';


const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 284,
  },
  bold: {
    fontWeight: 500,
  },
  grey: {
    color: theme.palette.greyScale[2],
  },

  // header + title stuff
  titleRow: {
    top: 0,
    flexShrink: 0,
    position: 'sticky',
    padding: '24px 24px 0',
    marginBottom: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.greyScale[7],
    zIndex: 1,
    '&::after': {
      content: "''",
      position: 'absolute',
      bottom: -8,
      left: 0,
      height: 8,
      width: '100%',
      backgroundImage: `linear-gradient(${theme.palette.greyScale[7]}, ${theme.applyOpacityToHex(theme.palette.greyScale[7], 0)})`,
    },
  },
  avatar: ({color}) => ({
    width: 80,
    height: 80,
    borderRadius: 8,
    flexGrow: 0,
    marginRight: 24,
    color: theme.palette.greyScale[7],
    backgroundColor: color,
    fontSize: 32,
    fontWeight: 900,
    flexShrink: 0,
  }),
  nameColumn: {
    flexGrow: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  buttonRow: {
    flexShrink: 0,
  },
  exitButton: {
    height: 34,
    width: 34,
    padding: 5,
    borderRadius: 17,
    '&:hover': {
      backgroundColor: theme.applyOpacityToHex(theme.palette.greyScale[0], 0.1),
    },
  },

  // graph
  graphSection: {
    margin: '0 12px',
    height: 208,
    flexShrink: 0,
  },

  centeredRow: {
    padding: '16px 24px',
    flexShrink: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  comingUp: {
    width: '100%',
    padding: 24,
    backgroundColor: theme.palette.color7.opacity30,
  },

  lowerSection: {
    width: '100%',
    padding: '32px 24px 24px',
  },

  zeroState: {
    margin: '36px 48px',
  },
}));


const ViewSeriesDialog = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { stModelId } = props.txn;

  const model = useSelector((state) => getScheduledTransactionById(state, stModelId));
  const transaction = model ? model.transaction : props.txn;
  const { history, txn } = useSelector((state) => getModelHistory(state, model));
  const { payee } = transaction;

  const isIncome = useSelector((state) => isIncomeTxn(state, transaction));
  const color = isIncome ? theme.palette.others.positive : theme.palette.color1.opacity100;
  const classes = useStyles({color});

  const negator = Math.sign(transaction.amount);
  const { chartData, average, sum } = generateChartData(history, negator);

  const historyDisplay = history.filter((historyTxn) => (historyTxn.source !== 'SCHEDULED_TRANSACTION_PENDING'))
    .map((historyTxn) => <ViewRow key={historyTxn.id} txn={historyTxn} isIncome={isIncome} past/>)
    .toIndexedSeq();

  const frequency = model && frequencyAliasFromRecurrence(model.recurrence);


  // +++     State values     +++
  const [editSeries, setEditSeries] = useState(false);
  const [openState, setOpenState] = useState(true);
  const [dirtyDialog, setDirtyDialog] = useState(false);

  const openEditSeries = () => setEditSeries(true);
  const closeEditDialog = (isDirty, close) => {
    if (close) {
      setEditSeries(false);
      onClose();
    }
    if (isDirty) {
      setDirtyDialog(true);
    } else {
      setEditSeries(false);
    }
  };
  function onClose() {
    setOpenState(false);
    dispatch(removeDialog());
  }

  return (
    <>
      <Dialog
        open={openState && !editSeries}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.root }}
        onClose={onClose}
      >
        <div className={classes.titleRow}>
          <Avatar className={classes.avatar}>
            {payee ? payee[0].toUpperCase() : '!'}
          </Avatar>
          <div className={classes.nameColumn}>
            <Typography variant="h5" className={classes.bold} style={{ width: '100%', padding: '8px 0' }} noWrap>
              {payee}
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {frequency}
            </Typography>
          </div>
          <div className={classes.buttonRow}>
            {model &&
              <QButton id="edit-series-btn" onClick={openEditSeries}>
                Edit Series
              </QButton>
            }

            <ButtonBase id="dlg-close" className={classes.exitButton} onClick={onClose} focusRipple >
              <ExitIcon />
            </ButtonBase>
          </div>
        </div>

        {model ?
          <>
            <div className={classes.graphSection}>
              <SeriesBarChart chartData={chartData} average={average} isIncome={isIncome}/>
            </div>

            <div className={classes.centeredRow}>
              <Typography variant="body1" className={classes.bold}>
                {`Total in Last 6 months : ${formatNumber(sum, undefined, '0,0', isIncome)}`}
              </Typography>
            </div>

            <div className={classes.comingUp}>
              <Typography variant="h6" style={{ paddingBottom: 8 }}>
                Coming Up
              </Typography>
              {txn.type ?
                <ViewRow txn={txn} isIncome={isIncome}/>
                :
                <SkeletonRow string2="You don't have anything coming up."/>
              }
            </div>

            <div className={classes.lowerSection}>
              <Typography variant="h6">
                History
              </Typography>

              {historyDisplay.size > 0 ?
                historyDisplay
                :
                <SkeletonRow string1="Blank Slate!" string2="your past is clear"/>
              }
            </div>
          </>
          :
          <div className={classes.zeroState}>
            <SkeletonRow string1="One Time Bill!" string2="there's no recurring model associated with this transaction"/>
          </div>
        }
      </Dialog>

      {editSeries &&
        <ScheduledTransactionEditDialog
          key={model ? (model.clientId || model.id) : 'noKey'}
          scheduledTransaction={model}
          onClose={closeEditDialog}
        />
      }

      {dirtyDialog &&
        <AlertDialog
          dialogId={'ignore this warning'}
          title="Just checking..."
          content="Are you sure you want to discard all changes?"
          onClose={(btnObj) => {
            if (btnObj.btnPressed === 'Discard') setEditSeries(false);
            setDirtyDialog(false);
          }}
          buttons={['Go Back', 'Discard']}
        />
      }
    </>
  );
};

ViewSeriesDialog.defaultProps = {
  txn: {},
};

ViewSeriesDialog.propTypes = {
  txn: PropTypes.object,
};

export default ViewSeriesDialog
